import React, { useContext } from "react";
import { kebabCase } from "lodash-es";

import Tooltip from "@kikoff/components/src/v1/info/Tooltip";
import { combineClasses } from "@kikoff/utils/src/string";

import styles from "./detail.module.scss";

interface DetailProps {
  label: React.ReactNode;
  info?: React.ReactNode;
  children: React.ReactNode;
  color?: React.CSSProperties["color"];
  textOnly?: boolean;
  className?: string;
  first?: boolean;
}

export default function Detail({
  className,
  label,
  textOnly,
  info,
  children,
  first,
  color,
}: DetailProps) {
  const valueTestId = kebabCase(label as string) + "-value";

  const { variant, propsFor, spacing } = useContext(DetailsGroupContext);

  return (
    <div
      className={combineClasses(
        styles.detail,
        styles[variant],
        textOnly && styles["text-only"],
        first && styles.first,
        className,
        propsFor.detail?.className
      )}
      style={{ "--spacing": spacing } as React.CSSProperties}
    >
      <header
        {...propsFor.label}
        className={combineClasses(
          !` ${propsFor.label?.className}`.includes(" text:") &&
            { default: "text:small" }[variant],
          propsFor.label?.className
        )}
      >
        {label}
        {info && (
          <Tooltip content={info}>
            <div className={styles.info}></div>
          </Tooltip>
        )}
      </header>
      <div
        {...propsFor.value}
        className={combineClasses(
          styles.value,
          !` ${propsFor.value?.className}`.includes(" text:") &&
            {
              default: "text:regular",
              "line-separated": "text:regular+",
              stacked: "text:regular+",
              "stacked-prominent": "text:large++",
            }[variant],
          propsFor.value?.className
        )}
        data-cy={valueTestId}
        style={{ color, ...propsFor.value?.style }}
      >
        {children}
      </div>
    </div>
  );
}

const DetailsGroupContext = React.createContext({
  variant: "default" as
    | "default"
    | "line-separated"
    | "stacked"
    | "stacked-prominent",
  spacing: null as string,
  propsFor: {} as {
    label?: React.ComponentPropsWithoutRef<"header">;
    value?: React.ComponentPropsWithoutRef<"div">;
    detail?: Partial<DetailProps>;
  },
});

interface DetailsGroupProps
  extends Partial<React.ContextType<typeof DetailsGroupContext>> {
  children: React.ReactNode;
}

export function DetailsGroup({ children, ...props }: DetailsGroupProps) {
  const context = useContext(DetailsGroupContext);

  return (
    <DetailsGroupContext.Provider value={{ ...context, ...props }}>
      {children}
    </DetailsGroupContext.Provider>
  );
}
